import {
  faCalendar,
  faCheck,
  faClock,
  faEye,
  faForward,
  faLink,
  faLinkSlash,
  faListCheck,
  faPlay,
  faScrewdriverWrench,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import Button from 'src/components/ui/Button';
import DateTile from 'src/components/ui/DateTile';
import ResourceItem from 'src/components/ui/ResourceItem';
import StatusBadge from 'src/components/ui/StatusBadge';
import {
  FULFILLMENT_STATUS,
  MAINTENANCE_EVENT_STATUS,
  MAINTENANCE_EVENT_STATUS_DISPLAY,
} from 'src/constants/maintenance';
import { useDateLocale } from 'src/hooks/useDateLocale';
import { pluralWord } from 'src/utils/stringUtils';

const TILE_COLOR = {
  [MAINTENANCE_EVENT_STATUS.PLANNED]: 'amber',
  [MAINTENANCE_EVENT_STATUS.IN_PROGRESS]: 'blue',
  [MAINTENANCE_EVENT_STATUS.COMPLETE]: 'green',
  [MAINTENANCE_EVENT_STATUS.CANCELLED]: 'grey',
};

/**
 * One maintenance event as a ResourceItem: date tile (colored by status),
 * "Maintenance Event N" title, dot status pill, meta line and chips. Fetches
 * its own fulfillments (+ linked downtime) for the progress/window summary —
 * the event serializer embeds none of these.
 */
const MaintenanceEventCard = ({
  event,
  name,
  fulfillments,
  downtime,
  usersByUri,
  canLinkDowntime,
  onTap,
  onResume,
  onDetails,
}) => {
  const { formattedFriendlyDateTime, formattedCompactWindow } = useDateLocale();

  const status = event.status;
  const display = MAINTENANCE_EVENT_STATUS_DISPLAY[status] || {};
  const isPlanned = status === MAINTENANCE_EVENT_STATUS.PLANNED;
  const isInProgress = status === MAINTENANCE_EVENT_STATUS.IN_PROGRESS;
  const isComplete = status === MAINTENANCE_EVENT_STATUS.COMPLETE;

  const total = fulfillments.length;
  const done = fulfillments.filter(f => f.status === FULFILLMENT_STATUS.COMPLETE).length;
  const skipped = fulfillments.filter(f => f.status === FULFILLMENT_STATUS.SKIPPED).length;

  // Events have no name column — "Maintenance Event N" (rapidfab parity).
  const title = name || 'Maintenance event';

  const tileDate = downtime?.start || event.started_at || event.completed_at || event.created;
  const leading = tileDate ? (
    <DateTile date={tileDate} color={TILE_COLOR[status] || 'grey'} />
  ) : null;

  let meta;
  if (isPlanned) {
    meta = downtime ? (
      <>
        <FontAwesomeIcon icon={faClock} />
        {formattedCompactWindow(downtime.start, downtime.finish)}
      </>
    ) : (
      <>
        <FontAwesomeIcon icon={faCalendar} /> No date set
      </>
    );
  } else if (isInProgress) {
    meta = (
      <>
        <FontAwesomeIcon icon={faClock} /> Started {formattedFriendlyDateTime(event.started_at)}
      </>
    );
  } else {
    const actor = usersByUri[event.completed_by || event.started_by];
    const actorName = actor?.name || actor?.username;
    meta = (
      <>
        <FontAwesomeIcon icon={faClock} />
        {actorName ? `${actorName} · ` : ''}
        {formattedFriendlyDateTime(event.completed_at || event.created)}
      </>
    );
  }

  const chips = (
    <>
      {canLinkDowntime &&
        (event.downtime ? (
          <StatusBadge size='sm' uppercase={false} variant='warning' icon={faLink}>
            Downtime
          </StatusBadge>
        ) : (
          <StatusBadge size='sm' uppercase={false} icon={faLinkSlash}>
            No downtime
          </StatusBadge>
        ))}
      {isComplete ? (
        <>
          <StatusBadge size='sm' uppercase={false} icon={faCheck}>
            {done} done
          </StatusBadge>
          {skipped > 0 && (
            <StatusBadge size='sm' uppercase={false} icon={faForward}>
              {skipped} skipped
            </StatusBadge>
          )}
        </>
      ) : (
        <StatusBadge size='sm' uppercase={false} icon={faListCheck}>
          {isInProgress
            ? `${done} of ${total} done`
            : `${total} ${pluralWord('requirement', total)}`}
        </StatusBadge>
      )}
    </>
  );

  const actions =
    isInProgress && (onDetails || onResume) ? (
      <>
        {onDetails && (
          <Button
            variant='primary'
            size='small'
            icon={<FontAwesomeIcon icon={faEye} />}
            onClick={e => {
              e.stopPropagation();
              onDetails();
            }}
          >
            Details
          </Button>
        )}
        {onResume && (
          <Button
            fill
            variant='success'
            size='small'
            icon={<FontAwesomeIcon icon={faPlay} />}
            onClick={e => {
              e.stopPropagation();
              onResume();
            }}
          >
            Resume
          </Button>
        )}
      </>
    ) : null;

  return (
    <ResourceItem
      leading={leading}
      icon={leading ? null : faScrewdriverWrench}
      title={title}
      status={
        <StatusBadge dot size='sm' uppercase={false} variant={display.variant}>
          {display.label}
        </StatusBadge>
      }
      meta={meta}
      chips={chips}
      actions={actions}
      onClick={onTap}
    />
  );
};

MaintenanceEventCard.propTypes = {
  event: PropTypes.object.isRequired,
  name: PropTypes.string,
  fulfillments: PropTypes.array,
  downtime: PropTypes.object,
  usersByUri: PropTypes.object,
  canLinkDowntime: PropTypes.bool,
  onTap: PropTypes.func,
  onResume: PropTypes.func,
  onDetails: PropTypes.func,
};

MaintenanceEventCard.defaultProps = {
  canLinkDowntime: true,
  name: null,
  fulfillments: [],
  downtime: null,
  usersByUri: {},
  onTap: null,
  onResume: null,
  onDetails: null,
};

export default MaintenanceEventCard;
